import React, { useEffect, useRef, useState, useMemo } from "react";
import { FaLocationDot } from "react-icons/fa6";
import ApiService from "../../services/ApiService";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AboutTab from "../../components/Profile/About";
import EditProfile from "../../components/Profile/Edit-Profile";
import Image from "../../components/Image";
import { User } from "lucide-react";
import Projects from "../../components/Company/Projects";
import Photos from "../../components/Company/photos";

const TalentView = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [projectData, setProjectData] = useState([]);
  const [photosData, setPhotosData] = useState([]);
  const [skillsData, setSkillsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);


  const location = useLocation();
  const talentId = location.state?.talentId;

  // Memoized tabs to prevent recreation on every render
  const tabs = useMemo(() => [
    { id: "about", label: t("new_text_27") },
    { id: "projects", label: t("t_menu_text2") },
    { id: "photos", label: t("t_my_Profile_tab4_text") },
  ], [t]);

  const getQueryParam = (key) => {
    return new URLSearchParams(location.search).get(key);
  };

  const [activeTab, setActiveTab] = useState(getQueryParam("subTab") || "about");

  useEffect(() => {
    const currentTab = getQueryParam("subTab");
    if (currentTab && currentTab !== activeTab) {
      setActiveTab(currentTab);
    }
  }, [location.search]);

  useEffect(() => {
    fetchProfile();
    fetchSkillsData();
  }, []);

  const fetchProfile = async () => {
    try {
      setIsLoading(true);
      const response = await ApiService.request({
        method: "GET",
        url: `/company/getTalentDetails?talent_id=${talentId}`,
      });
      const data = response.data;

      if (data.status) {
        setProfileData(data.data.talent);
        setProjectData(data.data.jobs);
        setPhotosData(data.data.photos);
      } else {
        toast.error(data.message);
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching profile:", error);
    }
  };

  const fetchSkillsData = async () => {
    try {
      const response = await ApiService.request({
        method: "GET",
        url: `getSkills`,
      });
      const data = response.data;

      if (data.status) {
        setSkillsData(data.data.skills);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error("Error fetching skills data:", error);
    }
  };

  const handleBack = async () => {
    setIsEditing(false);
    await fetchProfile();
  };

  return (
    <div className="inter-font" style={{ minHeight: "100vh" }}>
      {isEditing ? (
        <EditProfile onBack={handleBack} />
      ) : (
        <div
          className="inter-font container ps-md-5 py-4 bg-white rounded-4 shadow mt-4"
          style={{ maxWidth: "900px" }}
        >
          <div className="inter-font row align-items-start">
            {/* Left: Profile + Tabs */}
            <div className="inter-font col-md-2 text-center">
                <Image
                  src={profileData?.picture}
                  className="inter-font rounded-circle"
                  style={{ width: "100px", height: "100px", objectFit: "cover" }}
                  alt="Profile"
                />

              <div className="inter-font mb-3 mt-3 mt-md-5">
                <div className="d-flex flex-wrap justify-content-center justify-content-md-start" style={{ gap: "25px" }}>
                  {tabs.map((tab) => (
                    <div
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`inter-font d-flex flex-column align-items-center align-items-md-start ${
                        activeTab === tab.id ? "text-dark" : "text-muted"
                      }`}
                      style={{ cursor: "pointer", minWidth: "70px" }}
                    >
                      <div className="d-flex align-items-center gap-1">
                        <span>{tab.label}</span>
                      </div>
                      {activeTab === tab.id && (
                        <div
                          style={{
                            marginTop: "4px",
                            width: "25px",
                            height: "3px",
                            backgroundColor: "#CD496D",
                            borderRadius: "4px",
                          }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right: Content */}
            <div className="inter-font col-md-10">
              <div className="inter-font d-flex align-items-start justify-content-between">
                <div>
                  <h3 className="inter-font fw-bold">
                    {profileData?.first_name} {profileData?.last_name}
                  </h3>
                  <p className={`inter-font d-flex align-items-center gap-1 ${isRTL ? 'flex-row-reverse justify-content-end' : ''}`} style={{ color: "gray" }}>
                    <FaLocationDot className="inter-font me-1 text-dark" />
                    {[profileData?.nationality, profileData?.country].filter(Boolean).join(", ")}
                    {profileData?.country_flag && (
                      <img
                        src={profileData.country_flag}
                        alt={profileData?.country || "Country Flag"}
                        style={{ width: "35px", height: "25px", objectFit: "cover", borderRadius: "2px" }}
                        className="ms-2"
                      />
                    )}
                  </p>
                </div>
              </div>

              {isLoading ? (
                <div className="inter-font d-flex justify-content-center py-5">
                  <div className="inter-font spinner-border" role="status"></div>
                </div>
              ) : (
                <div className="inter-font tab-content mt-4">
                  {(activeTab === "about" && profileData) && <AboutTab profileData={profileData} />}
                  {activeTab === "projects" && <Projects projectData={projectData} />}
                  {activeTab === "photos" && <Photos projectData={photosData} />}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TalentView;